import { useRef, useMemo } from "react";
import useCharacterizationMetrics from "../core/hooks/useCharacterizationMetrics";
import HorizontalBarChart from "../../../components/charts/HorizontalBarChart";
import VerticalBarChart from "../../../components/charts/VerticalBarChart";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  formattedColorMaterial,
  toFrenchNumber,
} from "../../../components/formatted/FormattedText";
import { ReactComponent as Trash } from "../../../static/assets/Plastic-bin.svg";
import { ReactComponent as TrashBag } from "../../../static/assets/Garbage-bag.svg";
import { MaterialCountingPieChart } from "../../../components/charts/MaterialCountingPieChart";
import InfoAlert from "../../../components/InfoAlert";
import RegulationAlert from "./RegulationAlert";
import StatsCards from "./StatsCards";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import Cookies from "js-cookie";
import InfoTip from "../../../components/InfoTip";
import CoefficientsVersionToggle from "./CoefficientsVersionToggle";
import ConfrontationAnalysisSection from "./ConfrontationAnalysisSection";
import FinesDistributionChart from "./FinesDistributionChart";
import {
  getChar2AlertShown,
  setChar2AlertShown,
  CHAR2_ALERT_IDS,
} from "../core/utils/alertOncePerSession";
import {
  getParentCategoryLabelPlural,
  sortRepAssociatedKeys,
} from "../core/utils/utils";
import {
  getVisibleRegulationTypes,
  REGULATION_TYPES,
  getRegulationChipIndex,
} from "./constants/regulation";

const RegulationAlertOnce = ({ alertId, setChar2AlertShown, ...props }) => {
  const handleClose = () => {
    setChar2AlertShown(alertId);
  };
  return <RegulationAlert {...props} onClose={handleClose} />;
};

const InfoAlertOnce = ({ alertId, setChar2AlertShown, ...props }) => {
  const handleClose = () => {
    setChar2AlertShown(alertId);
  };
  return <InfoAlert {...props} onClose={handleClose} />;
};

const getVersionedFinesProportion = (
  versions,
  calculationDate,
  useLatest,
  defaultValue,
) => {
  if (!versions || versions.length === 0) return defaultValue ?? 0;
  if (useLatest) {
    const sorted = [...versions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    return sorted[0]?.value ?? defaultValue ?? 0;
  }
  if (!calculationDate) return defaultValue ?? 0;
  const requestedDate = new Date(calculationDate);
  if (Number.isNaN(requestedDate.getTime())) return defaultValue ?? 0;
  const inEffect = versions
    .filter((v) => new Date(v.date) <= requestedDate)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  return inEffect.length > 0 ? inEffect[0].value : defaultValue ?? 0;
};

const getDefaultRegulation = (t) => ({
  label: t("No regulation"),
  description: t(
    "Complete view of the characterization: mass, volume, waste stream, object count and size analyses. This tab provides an exhaustive reading of the waste stream, without restriction to any specific regulation.",
  ),
  chips: [
    t("No regulation"),
    `${t("Decree")} n° 2021-1199`,
    t("France EPR recycling channel"),
    t("OMR"),
  ],
});

const getRegulationDetails = (t) => ({
  [t("No regulation")]: {
    title: t("No regulation"),
    description: t(
      "Complete view of the characterization: mass, volume, waste stream, object count and size analyses.\nThis tab provides an exhaustive reading of the waste stream, without restriction to any specific regulation.",
    ),
  },
  [`${t("Decree")} n° 2021-1199`]: {
    title: `${t("Decree")} n° 2021-1199`,
    description: t(
      "Regulatory mass table in accordance with Decree No. 2021-1199 of 09/16/21.\nIt governs the characterization of household and similar waste and defines the sorting categories to follow.",
    ),
  },
  [t("France EPR recycling channel")]: {
    title: t("France EPR recycling channel"),
    description: t(
      "Analysis of the waste stream through the lens of Extended Producer Responsibility:\nbreakdown of materials across the different EPR channels (packaging, furniture, electrical equipment, etc.).",
    ),
  },
  [t("OMR")]: {
    title: t("OMR"),
    description: t(
      'RHW approval concerns the collection and treatment of waste that remains after sorting (household "mixed waste").\nThis waste is not recyclable and is generally sent for incineration or landfilling in facilities approved by the State.',
    ),
  },
});

const CharacterizationReports = ({
  totalWeight = 0,
  totalValueEur = 0,
  materialDelivery = [],
  totalWasteMass = 0,
  totalWasteVolume = 0,
  recoverableMaterialList = [],
  parentCategoriesList = [],
  totalVolumeRecoverable = 0,
  totalMassRecoverable = 0,
  recoverableWithOutlet = 0,
  recoverableWithNoOutlet = 0,
  materialTotal = 0,
  outletData = {},
  repData = {},
  characterizationType = "1199",
  containerRegulationType = null,
  onRegulationChange,
  refs = {},
  sizeRangeDistribution = null,
  sizeRangeMatrix = null,
  sizeThreshold = 40,
  totalFinesMass = 0,
  totalFinesVolume = 0,
  finesBelowThreshold = {},
  finesBelowThresholdByMaterial = {},
  keptVolumeByMaterialFp0 = {},
  keptVolumeByMaterialFpCalculated = {},
  zone = null,
  period = null,
  clientParsed = null,
  useLatestCoefficients = false,
  hasNewerCoefficients = false,
  coefficientsVersion = "historical",
  coefficientsDate = null,
  latestCoefficientsDate = null,
  onCoefficientsToggle,
  coefficientsLoading = false,
  isBenneCharacterization = false,
}) => {
  const massChartRef = useRef(null);
  const massTableRef = useRef(null);
  const outletChartRef = useRef(null);
  const outletTableRef = useRef(null);
  const volumeChartRef = useRef(null);
  const objectsChartRef = useRef(null);
  const repChartRef = useRef(null);
  const repTableRef = useRef(null);

  const finalRefs = {
    massChartRef: refs.massChartRef || massChartRef,
    massTableRef: refs.massTableRef || massTableRef,
    outletChartRef: refs.outletChartRef || outletChartRef,
    outletTableRef: refs.outletTableRef || outletTableRef,
    volumeChartRef: refs.volumeChartRef || volumeChartRef,
    objectsChartRef: refs.objectsChartRef || objectsChartRef,
    repChartRef: refs.repChartRef || repChartRef,
    repTableRef: refs.repTableRef || repTableRef,
  };
  const metrics = useCharacterizationMetrics({
    materialDelivery,
    totalWasteMass,
    totalWasteVolume,
    recoverableMaterialList,
    parentCategoriesList,
    totalVolumeRecoverable,
    totalMassRecoverable,
    recoverableWithOutlet,
    recoverableWithNoOutlet,
  });
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const regulationDetails = getRegulationDetails(t);
  const currentRegulation = getDefaultRegulation(t);

  const hasWeight =
    totalWeight != null && totalWeight !== "" && Number(totalWeight) !== 0;

  const effectiveThresholdCm = useMemo(() => {
    if (characterizationType === "1199") return 40;
    if (characterizationType === "RHW" || characterizationType === "OMR")
      return 0;
    return typeof sizeThreshold === "number" ? sizeThreshold : 40;
  }, [characterizationType, sizeThreshold]);

  const materialsByReferenceName = useMemo(() => {
    try {
      const list = JSON.parse(
        localStorage.getItem("materialsDescription") || "[]",
      );
      const calculationDate =
        period?.periodMaterialStopSubfilter ||
        period?.periodMaterialStop ||
        period?.start ||
        period?._time;
      const useLatest = useLatestCoefficients === true;
      return (list || []).reduce((acc, m) => {
        if (m?.referenceName != null) {
          const finesProportion = getVersionedFinesProportion(
            m.finesProportionVersions,
            calculationDate,
            useLatest,
            m.finesProportion ?? 0,
          );
          acc[m.referenceName] = {
            density: m.density,
            finesProportion,
          };
        }
        return acc;
      }, {});
    } catch {
      return {};
    }
  }, [
    period?.periodMaterialStopSubfilter,
    period?.periodMaterialStop,
    period?.start,
    period?._time,
    useLatestCoefficients,
  ]);

  const volumetricTableRows = useMemo(() => {
    const breakdown = finesBelowThresholdByMaterial || {};
    const allCls = new Set([
      ...Object.keys(keptVolumeByMaterialFp0 || {}),
      ...Object.keys(breakdown),
    ]);
    return Array.from(allCls)
      .filter((cls) => cls)
      .map((cls) => ({
        cls,
        pctAboveThreshold:
          totalWasteVolume > 0
            ? ((keptVolumeByMaterialFp0?.[cls] || 0) / totalWasteVolume) * 100
            : 0,
        pctBelowThreshold:
          totalWasteVolume > 0
            ? ((breakdown[cls]?.volumeObject || 0) / totalWasteVolume) * 100
            : 0,
      }))
      .filter((r) => r.pctAboveThreshold > 0 || r.pctBelowThreshold > 0)
      .sort((a, b) => t(a.cls).localeCompare(t(b.cls), i18n.language || "fr"));
  }, [
    finesBelowThresholdByMaterial,
    keptVolumeByMaterialFp0,
    totalWasteVolume,
    t,
    i18n.language,
  ]);

  const viewMode = Cookies.get("viewMode") || "client";
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const isAdmin = isAkanthasAccount && viewMode === "admin";

  const visibleRegulationTypes = getVisibleRegulationTypes(
    containerRegulationType,
  );

  const selectedRegulationIndex = getRegulationChipIndex(characterizationType);
  const isOmrRegulation =
    characterizationType === REGULATION_TYPES.RHW ||
    characterizationType === "OMR";
  const showDecree1199MassTable =
    !isOmrRegulation && characterizationType !== "EPR";
  const showEprMassTable =
    characterizationType === "EPR" &&
    Boolean(repData?.data) &&
    Object.keys(repData.data).length > 0;

  // Calculate fines values (same logic as in characterisation module)
  const finesMass =
    (finesBelowThreshold?.massObject || 0) + (totalFinesMass || 0);
  const finesVolume =
    (finesBelowThreshold?.volumeObject || 0) + (totalFinesVolume || 0);
  const finesCount = finesBelowThreshold?.count || 0;

  const hasFines = finesMass > 0 || finesVolume > 0 || finesCount > 0;

  const finesLabel =
    effectiveThresholdCm > 0
      ? t("Fines (below {{threshold}} cm)", {
          threshold: effectiveThresholdCm,
        })
      : t("Fines");

  // Add fines to mass metrics
  const massLabels = [
    ...(metrics?.mass?.labels ?? []),
    ...(hasFines ? [finesLabel] : []),
  ];
  const massValues = [
    ...(metrics?.mass?.values ?? []),
    ...(hasFines && totalWasteMass > 0
      ? [(finesMass / totalWasteMass) * 100]
      : []),
  ];
  // Use default blue color for all materials, including fines
  const massColors = [
    ...(metrics?.mass?.colors ?? []),
    ...(hasFines ? ["rgba(75,192,192,0.6)"] : []), // Blue color for fines
  ];

  // Add fines to volume metrics
  const volumeLabels = [
    ...(metrics?.volume?.labels ?? []),
    ...(hasFines ? [finesLabel] : []),
  ];
  const volumeValues = [
    ...(metrics?.volume?.values ?? []),
    ...(hasFines && totalWasteVolume > 0
      ? [(finesVolume / totalWasteVolume) * 100]
      : []),
  ];

  // Add fines to objects data
  const dataNumberOfMaterial = [
    ...materialDelivery.map((material) => material.count),
    ...(hasFines ? [finesCount] : []),
  ];
  const labelMaterialSeparate = [
    ...materialDelivery.map((material) => t(material.cls)),
    ...(hasFines ? [finesLabel] : []),
  ];
  const colorMaterial = [
    ...materialDelivery.map((material) => formattedColorMaterial(material.cls)),
    ...(hasFines ? ["#FFA500"] : []), // Orange color for fines
  ];

  const outletChartData = useMemo(() => {
    const labels = [...(outletData?.labels ?? [])];
    const data = [...(outletData?.data ?? [])];
    const baseColors = ["#f58142", "#42f55d", "#bf42f5", "#2ab5b8"];
    if (hasFines && finesMass > 0) {
      const landfillLabel = t("Stockage");
      const landfillIndex = labels.findIndex(
        (label) => label === landfillLabel,
      );
      const finesTonnes = finesMass / 1000;
      if (landfillIndex >= 0) {
        data[landfillIndex] = (data[landfillIndex] || 0) + finesTonnes;
      } else {
        labels.push(landfillLabel);
        data.push(finesTonnes);
      }
    }
    if (data.length === 0) return null;
    const colors =
      data.length > baseColors.length ? [...baseColors, "#2ab5b8"] : baseColors;
    return { labels, data, colors };
  }, [outletData, hasFines, finesMass, t]);

  return (
    <div className="char-reports">
      <div className="space-y-6">
        <div className="char-reports-regulation-chips">
          {visibleRegulationTypes.map((regulationType) => (
            <button
              key={regulationType}
              type="button"
              onClick={() => onRegulationChange?.(regulationType)}
              className={`char-reports-regulation-chips-item ${
                regulationType === characterizationType
                  ? "char-reports-regulation-chips-item-active"
                  : ""
              }`}
            >
              {currentRegulation.chips[getRegulationChipIndex(regulationType)]}
            </button>
          ))}
        </div>
        {!getChar2AlertShown(CHAR2_ALERT_IDS.REGULATION_MAIN) && (
          <RegulationAlertOnce
            alertId={CHAR2_ALERT_IDS.REGULATION_MAIN}
            setChar2AlertShown={setChar2AlertShown}
            title={
              regulationDetails[
                currentRegulation.chips[selectedRegulationIndex]
              ]?.title || currentRegulation.chips[selectedRegulationIndex]
            }
            description={
              regulationDetails[
                currentRegulation.chips[selectedRegulationIndex]
              ]?.description || currentRegulation.description
            }
            icon={
              <Trash className="w-[38.26px] h-[60.82px] ml-[6.5px] mr-[6.5px]" />
            }
            iconAlt="recycle-bin"
          />
        )}
        {characterizationType === "1199" &&
          !getChar2AlertShown(CHAR2_ALERT_IDS.REGULATION_1199_SORTING) && (
            <RegulationAlertOnce
              alertId={CHAR2_ALERT_IDS.REGULATION_1199_SORTING}
              setChar2AlertShown={setChar2AlertShown}
              title={t("Sorting rule")}
              description={t(
                "All objects whose dimension is less than 40 cm are classified in the « Fine » category.",
              )}
              icon={<TrashBag className="w-[50.54px] h-[42px]" />}
              iconAlt="trash-bag"
            />
          )}
        {isAdmin &&
          (hasNewerCoefficients || useLatestCoefficients) &&
          typeof onCoefficientsToggle === "function" && (
            <CoefficientsVersionToggle
              useLatestCoefficients={useLatestCoefficients}
              onToggle={onCoefficientsToggle}
              hasNewerCoefficients={hasNewerCoefficients}
              coefficientsVersion={coefficientsVersion}
              coefficientsDate={coefficientsDate}
              latestCoefficientsDate={latestCoefficientsDate}
              isLoading={coefficientsLoading}
            />
          )}
        <div className="char-reports-section">
          <h2 className="char-reports-section-title">{t("Mass analysis")}</h2>
          {characterizationType !== REGULATION_TYPES.DECREE_1199 &&
            hasWeight &&
            totalWasteMass > 0 && (
              <div className="mt-0 mb-0">
                <StatsCards
                  recoverablePercentage={toFrenchNumber(
                    (totalMassRecoverable / totalWasteMass) * 100,
                    1,
                  )}
                  withChannelPercentage={toFrenchNumber(
                    (recoverableWithOutlet / totalWasteMass) * 100,
                    1,
                  )}
                  withoutChannelPercentage={toFrenchNumber(
                    (recoverableWithNoOutlet / totalWasteMass) * 100,
                    1,
                  )}
                />
              </div>
            )}
          {hasWeight &&
            characterizationType === "1199" &&
            !getChar2AlertShown(CHAR2_ALERT_IDS.INFO_NON_COMPLIANT) && (
              <div className="mt-0 mb-6">
                <InfoAlertOnce
                  alertId={CHAR2_ALERT_IDS.INFO_NON_COMPLIANT}
                  setChar2AlertShown={setChar2AlertShown}
                  question={t("How to know if a container is non-compliant?")}
                  answer={t(
                    "When the mass percentage of a waste category and the channel share exceed 30%.",
                  )}
                />
              </div>
            )}
          {!hasWeight ? (
            <div className="char-reports-placeholder-no-weight rounded-lg bg-[#E6ECFD] border-2 border-[#0136BD] p-6 flex items-center justify-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-7 h-7 rounded-full border-[3px] border-[#0136BD] flex items-center justify-center leading-none">
                  <span className="text-[#0136BD] font-bold text-base leading-none inline-flex items-center justify-center w-full h-full">
                    i
                  </span>
                </div>
              </div>
              <p className="char-text-14 text-[#0136BD] font-medium m-0">
                {t(
                  "Please provide the total weight to unlock mass proportions",
                )}
              </p>
            </div>
          ) : (
            <div
              className={`char-reports-chart-container ${
                characterizationType === "EPR"
                  ? "char-reports-chart-container--epr"
                  : showDecree1199MassTable
                    ? "char-reports-chart-container--default"
                    : "char-reports-chart-container--omr"
              }`}
            >
              <div
                ref={finalRefs.massChartRef}
                className="char-reports-chart-container-item"
              >
                <HorizontalBarChart
                  key={characterizationType}
                  dataChart={massValues}
                  labelsChart={massLabels}
                  unitValueChart="%"
                  colorChart={massColors}
                />
              </div>
              {(showEprMassTable || showDecree1199MassTable) && (
                <div
                  ref={finalRefs.massTableRef}
                  className="char-reports-table-container"
                >
                  {showEprMassTable ? (
                    <section className="w-full flex flex-col char-reports-table--rep">
                      <div className="char-reports-table-header char-reports-table-header--rep">
                        <div className="char-reports-table-rep-col char-reports-table-rep-col-label">
                          {t("Associated EPR")}
                        </div>
                        <div className="char-reports-table-rep-col char-reports-table-rep-col-pct">
                          {t("Mass")} (%)
                        </div>
                        <div className="char-reports-table-rep-col char-reports-table-rep-col-kg">
                          {t("Mass")} (kg)
                        </div>
                      </div>
                      <div className="pb-0">
                        {sortRepAssociatedKeys(
                          Object.keys(repData.data),
                          t,
                        ).map((key, index) => (
                          <div
                            key={`rep-${index}`}
                            className="char-reports-table-row char-reports-table-row--rep"
                          >
                            <div className="char-reports-table-rep-col char-reports-table-rep-col-label">
                              <div className="char-reports-table-rep-label">
                                <span
                                  className="char-reports-table-rep-label-title"
                                  style={{
                                    textTransform:
                                      key.length <= 3 ? "uppercase" : "none",
                                    fontWeight: "normal",
                                  }}
                                >
                                  {t(key)}
                                </span>
                                {(repData.data[key]?.objects?.length > 0
                                  ? repData.data[key].objects
                                      .map((material) => t(material))
                                      .sort()
                                      .join(", ")
                                  : repData.data[key]?.description) && (
                                  <span className="char-reports-table-rep-label-sub">
                                    {repData.data[key]?.objects?.length > 0
                                      ? repData.data[key].objects
                                          .map((material) => t(material))
                                          .sort()
                                          .join(", ")
                                      : repData.data[key]?.description}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="char-reports-table-rep-col char-reports-table-rep-col-pct">
                              {repData.data[key]?.massObject &&
                              totalWasteMass > 0
                                ? toFrenchNumber(
                                    (repData.data[key].massObject /
                                      totalWasteMass) *
                                      100,
                                    1,
                                  )
                                : "0,0"}
                              %
                            </div>
                            <div className="char-reports-table-rep-col char-reports-table-rep-col-kg">
                              {totalWeight !== "" &&
                              repData.data[key]?.massObject &&
                              totalWasteMass > 0
                                ? toFrenchNumber(
                                    (repData.data[key].massObject /
                                      totalWasteMass) *
                                      totalWeight,
                                    1,
                                  )
                                : "-"}{" "}
                              {totalWeight !== "" && "kg"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : (
                    <section className="w-full flex flex-col">
                      <div className="char-reports-table-header">
                        <div className="char-reports-table-header-label char-reports-table-header-col-3-5">
                          {t("Type de déchets")}
                        </div>
                        <div className="char-reports-table-header-col-2-5">
                          <div className="char-reports-table-row-cell-half">
                            {t("Masse (kg)")}
                          </div>
                          <div className="char-reports-table-row-cell-half">
                            {t("Masse (%)")}
                          </div>
                        </div>
                      </div>
                      <div className="pb-0">
                        {parentCategoriesList.map(
                          (parentCategory, indexParentCategory) => (
                            <div
                              key={`parentcat-${indexParentCategory}`}
                              className="char-reports-table-row"
                            >
                              <div className="char-reports-table-row-cell char-reports-table-row-cell-3-5">
                                <div
                                  className="char-text-14"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  {capitalizeFirstLetter(
                                    getParentCategoryLabelPlural(
                                      t,
                                      parentCategory.parentCategory,
                                    ),
                                  )}
                                  {parentCategory.categoryList &&
                                    parentCategory.categoryList.length > 0 && (
                                      <InfoTip widthMaxRem={20}>
                                        <div>
                                          {[
                                            ...new Set(
                                              parentCategory.categoryList,
                                            ),
                                          ]
                                            .map((item) => t(item))
                                            .sort()
                                            .join(", ")}
                                        </div>
                                      </InfoTip>
                                    )}
                                </div>
                              </div>
                              <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                                <div className="char-reports-table-row-cell-half">
                                  {totalWeight !== ""
                                    ? toFrenchNumber(
                                        (parentCategory.massObjects /
                                          totalWasteMass) *
                                          totalWeight,
                                        1,
                                      )
                                    : "-"}
                                </div>
                                <div className="char-reports-table-row-cell-half">
                                  {totalWasteMass > 0
                                    ? toFrenchNumber(
                                        (parentCategory.massObjects /
                                          totalWasteMass) *
                                          100,
                                        1,
                                      )
                                    : "0,0"}
                                  %
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                        {hasFines && (
                          <div
                            key="fines-row"
                            className="char-reports-table-row"
                          >
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-3-5">
                              <div
                                className="char-text-14"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {t("Autres (FINES)")}
                                <InfoTip widthMaxRem={20}>
                                  <div>{finesLabel}</div>
                                </InfoTip>
                              </div>
                            </div>
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                              <div className="char-reports-table-row-cell-half">
                                {totalWeight !== "" && totalWasteMass > 0
                                  ? toFrenchNumber(
                                      (finesMass / totalWasteMass) *
                                        totalWeight,
                                      1,
                                    )
                                  : "-"}
                              </div>
                              <div className="char-reports-table-row-cell-half">
                                {totalWasteMass > 0
                                  ? toFrenchNumber(
                                      (finesMass / totalWasteMass) * 100,
                                      1,
                                    )
                                  : "0,0"}
                                %
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {!["EPR"].includes(characterizationType) && (
          <div className="char-reports-section">
            <h2 className="char-reports-section-title">
              {t("Analysis by outlet")}
            </h2>
            {!hasWeight ? (
              <div className="char-reports-placeholder-no-weight rounded-lg bg-[#E6ECFD] border-2 border-[#0136BD] p-6 flex items-center justify-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-7 h-7 rounded-full border-[3px] border-[#0136BD] flex items-center justify-center leading-none">
                    <span className="text-[#0136BD] font-bold text-base leading-none inline-flex items-center justify-center w-full h-full">
                      i
                    </span>
                  </div>
                </div>
                <p className="char-text-14 text-[#0136BD] font-medium m-0">
                  {t(
                    "Please provide the total weight to unlock outlet proportions",
                  )}
                </p>
              </div>
            ) : (
              <div className="char-reports-chart-container">
                <div
                  ref={finalRefs.outletChartRef}
                  className="char-reports-chart-container-item char-reports-chart-container-item-large"
                >
                  {outletChartData && (
                    <div className="char-reports-pie-wrapper">
                      <MaterialCountingPieChart
                        dataChart={outletChartData.data}
                        labelChart={outletChartData.labels}
                        materialTotal={totalWasteMass / 1000}
                        centerDisplayValue={
                          hasWeight ? Number(totalWeight) / 1000 : undefined
                        }
                        colorChart={outletChartData.colors}
                        showMaterialTotal={true}
                        radius={["30%", "60%"]}
                        title={t("Analyzed tonnes")}
                      />
                    </div>
                  )}
                </div>
                <div
                  ref={finalRefs.outletTableRef}
                  className="char-reports-table-container"
                >
                  <section className="w-full flex flex-col">
                    <div className="char-reports-table-header">
                      <div className="char-reports-table-header-label char-reports-table-header-col-1-3">
                        {t("Type de déchets")}
                      </div>
                      <div className="char-reports-table-header-col-2-3">
                        <div className="char-reports-table-row-cell-half">
                          {t("With recycling channel")}
                        </div>
                        <div className="char-reports-table-row-cell-half">
                          {t("Without recycling channel")}
                        </div>
                      </div>
                    </div>
                    <div className="pb-0">
                      {parentCategoriesList.map(
                        (parentCategory, indexParentCategory) => (
                          <div
                            key={`parentcat-${indexParentCategory}`}
                            className="char-reports-table-row"
                          >
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-1-3">
                              <div
                                className="char-text-14"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {capitalizeFirstLetter(
                                  getParentCategoryLabelPlural(
                                    t,
                                    parentCategory.parentCategory,
                                  ),
                                )}
                                {parentCategory.categoryList &&
                                  parentCategory.categoryList.length > 0 && (
                                    <InfoTip widthMaxRem={20}>
                                      <div>
                                        {[
                                          ...new Set(
                                            parentCategory.categoryList,
                                          ),
                                        ]
                                          .map((item) => t(item))
                                          .sort()
                                          .join(", ")}
                                      </div>
                                    </InfoTip>
                                  )}
                              </div>
                            </div>
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-2-3">
                              <div className="char-reports-table-row-cell-col">
                                <span className="char-reports-table-row-cell-col-main">
                                  {totalWeight !== ""
                                    ? toFrenchNumber(
                                        (parentCategory.massInOutlet /
                                          totalWasteMass) *
                                          totalWeight,
                                        1,
                                      )
                                    : "-"}{" "}
                                  {totalWeight !== "" && "kg"}
                                </span>
                                <span className="char-reports-table-row-cell-col-sub">
                                  {parentCategory.massInOutlet > 0
                                    ? toFrenchNumber(
                                        (parentCategory.massInOutlet /
                                          totalWasteMass) *
                                          100,
                                        1,
                                      )
                                    : "0,0"}
                                  %
                                </span>
                              </div>
                              <div className="char-reports-table-row-cell-col">
                                <span className="char-reports-table-row-cell-col-main">
                                  {totalWeight !== ""
                                    ? toFrenchNumber(
                                        (parentCategory.massNotInOutlet /
                                          totalWasteMass) *
                                          totalWeight,
                                        1,
                                      )
                                    : "-"}{" "}
                                  {totalWeight !== "" && "kg"}
                                </span>
                                <span className="char-reports-table-row-cell-col-sub">
                                  {parentCategory.massNotInOutlet > 0
                                    ? toFrenchNumber(
                                        (parentCategory.massNotInOutlet /
                                          totalWasteMass) *
                                          100,
                                        1,
                                      )
                                    : "0,0"}
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                      {/* Add fines row if hasFines */}
                      {hasFines && (
                        <div
                          key="fines-row-outlet"
                          className="char-reports-table-row"
                        >
                          <div className="char-reports-table-row-cell char-reports-table-row-cell-1-3">
                            <div
                              className="char-text-14"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {t("Autres (FINES)")}
                              <InfoTip widthMaxRem={20}>
                                <div>{finesLabel}</div>
                              </InfoTip>
                            </div>
                          </div>
                          <div className="char-reports-table-row-cell char-reports-table-row-cell-2-3">
                            <div className="char-reports-table-row-cell-col">
                              <span className="char-reports-table-row-cell-col-main">
                                {toFrenchNumber(0, 1)} kg
                              </span>
                              <span className="char-reports-table-row-cell-col-sub">
                                0,0%
                              </span>
                            </div>
                            <div className="char-reports-table-row-cell-col">
                              <span className="char-reports-table-row-cell-col-main">
                                {totalWeight !== ""
                                  ? toFrenchNumber(
                                      (finesMass / totalWasteMass) *
                                        totalWeight,
                                      1,
                                    )
                                  : "0,0"}{" "}
                                kg
                              </span>
                              <span className="char-reports-table-row-cell-col-sub">
                                {totalWasteMass > 0
                                  ? toFrenchNumber(
                                      (finesMass / totalWasteMass) * 100,
                                      1,
                                    )
                                  : "0,0"}
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>
            )}
          </div>
        )}
        {!["1199", "EPR"].includes(characterizationType) && (
          <div className="char-reports-section">
            <div className="w-full flex flex-row gap-4">
              <div className="w-1/2">
                <h2 className="char-reports-section-title">
                  {t("Volume analysis")}
                </h2>
              </div>
              <div className="w-1/2">
                <h2 className="char-reports-section-title">
                  {t("Objects distribution")}
                </h2>
              </div>
            </div>
            <div
              className={`char-reports-chart-container ${
                characterizationType === "EPR"
                  ? "char-reports-chart-container--epr"
                  : "char-reports-chart-container--default"
              }`}
            >
              <div
                ref={finalRefs.volumeChartRef}
                className="char-reports-chart-container-item"
              >
                <HorizontalBarChart
                  dataChart={volumeValues}
                  labelsChart={volumeLabels}
                  unitValueChart="%"
                />
              </div>
              <div
                ref={finalRefs.objectsChartRef}
                className="char-reports-table-container"
              >
                {materialTotal !== undefined && materialTotal !== 0 && (
                  <div className="char-reports-pie-wrapper">
                    <MaterialCountingPieChart
                      dataChart={dataNumberOfMaterial}
                      labelChart={labelMaterialSeparate}
                      materialTotal={materialTotal}
                      colorChart={colorMaterial}
                      showMaterialTotal={true}
                      radius={["30%", "60%"]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {characterizationType === "EPR" &&
          repData?.data &&
          Object.keys(repData.data).length > 0 && (
            <div className="char-reports-section">
              <h2 className="char-reports-section-title">
                {t("Volume analysis")}
              </h2>
              <div className="flex justify-center">
                <div
                  ref={finalRefs.repChartRef}
                  className="char-reports-chart-full"
                >
                  {repData?.repChartData && repData.repChartData.length > 0 && (
                    <div className="char-reports-pie-wrapper">
                      <MaterialCountingPieChart
                        dataChart={repData.repChartData.map((item) =>
                          parseFloat(item.value),
                        )}
                        labelChart={repData.repChartData.map(
                          (item) => item.name,
                        )}
                        materialTotal={100}
                        colorChart={[
                          "#f58142",
                          "#42f55d",
                          "#bf42f5",
                          "#2ab5b8",
                        ]}
                        showMaterialTotal={false}
                        title={t("Value by material")}
                        radius={["13%", "26%"]}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        {isAdmin && (
          <div className="char-reports-section">
            <h2 className="char-reports-section-title">
              {t("Economic analysis")}
            </h2>
            <div className="char-reports-chart-container">
              <div className="char-reports-chart-container-item char-reports-chart-container-item-large">
                {(() => {
                  const withValue = (materialDelivery || []).filter(
                    (m) => (m.valueEur ?? 0) > 0,
                  );
                  const economicData = withValue.map((m) => m.valueEur ?? 0);
                  const economicLabels = withValue.map((m) => t(m.cls));
                  const economicColors = withValue.map((m) =>
                    formattedColorMaterial(m.cls),
                  );
                  const totalEur = totalValueEur ?? 0;
                  return totalEur > 0 && economicData.length > 0 ? (
                    <div className="char-reports-pie-wrapper">
                      <MaterialCountingPieChart
                        dataChart={economicData}
                        labelChart={economicLabels}
                        materialTotal={totalEur}
                        colorChart={economicColors}
                        showMaterialTotal={true}
                        radius={["30%", "60%"]}
                        title={t("Value by material")}
                        titleFontSize="1.25rem"
                        titleFontWeight={700}
                      />
                    </div>
                  ) : (
                    <p
                      style={{
                        color: "#666",
                        fontStyle: "italic",
                        padding: "24px",
                      }}
                    >
                      {t(
                        "No economic value to display. Configure material costs in settings to see the breakdown.",
                      )}
                    </p>
                  );
                })()}
              </div>
              <div className="char-reports-table-container">
                <section className="w-full flex flex-col">
                  <div className="char-reports-table-header">
                    <div className="char-reports-table-header-label char-reports-table-header-col-3-5">
                      {t("Type de déchets")}
                    </div>
                    <div className="char-reports-table-header-col-2-5">
                      <div className="char-reports-table-row-cell-half">
                        {t("Value")} (€)
                      </div>
                      <div className="char-reports-table-row-cell-half">
                        {t("Share (%)")}
                      </div>
                    </div>
                  </div>
                  <div className="pb-0">
                    {(materialDelivery || [])
                      .filter((m) => (m.valueEur ?? 0) > 0)
                      .sort((a, b) => (b.valueEur ?? 0) - (a.valueEur ?? 0))
                      .map((material, idx) => {
                        const totalEur = totalValueEur ?? 0;
                        const pct =
                          totalEur > 0
                            ? ((material.valueEur ?? 0) / totalEur) * 100
                            : 0;
                        return (
                          <div
                            key={`economic-${material.cls}-${idx}`}
                            className="char-reports-table-row"
                          >
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-3-5">
                              <div className="char-text-14">
                                {capitalizeFirstLetter(t(material.cls))}
                              </div>
                            </div>
                            <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                              <div className="char-reports-table-row-cell-half">
                                {toFrenchNumber(material.valueEur ?? 0, 2)} €
                              </div>
                              <div className="char-reports-table-row-cell-half">
                                {toFrenchNumber(pct, 1)} %
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {(materialDelivery || []).filter(
                      (m) => (m.valueEur ?? 0) > 0,
                    ).length === 0 && (
                      <div className="char-reports-table-row">
                        <div
                          className="char-reports-table-row-cell char-reports-table-row-cell-3-5"
                          style={{ color: "#999", fontStyle: "italic" }}
                        >
                          {t("No data")}
                        </div>
                        <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                          -
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
        {isAdmin && (
          <ConfrontationAnalysisSection
            parentCategoriesList={parentCategoriesList}
            totalWasteMass={totalWasteMass}
            totalWeight={totalWeight}
            hasFines={hasFines}
            finesMassPercent={
              hasFines && totalWasteMass > 0
                ? (finesMass / totalWasteMass) * 100
                : 0
            }
            period={period}
            clientParsed={clientParsed}
            zone={zone}
          />
        )}
        {/* Size Distribution Section - classic zone only (not carac en benne) */}
        {characterizationType === REGULATION_TYPES.CLASSIC &&
          !isBenneCharacterization &&
          sizeRangeDistribution && (
            <div className="char-reports-section">
              <h2 className="char-reports-section-title">
                {t("Object size distribution")}
              </h2>
              <div className="char-card">
                <details className="mb-4 rounded-lg border border-[#00A7A6] bg-[rgba(0,167,166,0.10)]">
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#007A79]">
                    {t("About this chart")}
                  </summary>
                  <div className="px-4 pb-4 text-sm text-gray-900">
                    {effectiveThresholdCm === 0
                      ? t("size_distribution_info_no_cutoff")
                      : t("size_distribution_info_configured", {
                          thresholdCm: effectiveThresholdCm,
                        })}{" "}
                    <button
                      type="button"
                      className="text-[#00A7A6] font-semibold underline underline-offset-2 hover:opacity-90"
                      onClick={() =>
                        history.push("/settings", { showConstant: true })
                      }
                    >
                      {t("Settings")}
                    </button>
                  </div>
                </details>

                <div style={{ height: "500px" }}>
                  {(() => {
                    const filtered = (sizeRangeDistribution.labels || [])
                      .map((label, idx) => ({
                        label,
                        value: (sizeRangeDistribution.data || [])[idx],
                      }))
                      .filter((item) => item.label !== "total");

                    return (
                      <VerticalBarChart
                        dataChart={filtered.map((x) => x.value)}
                        labelsChart={filtered.map((x) => `${x.label}m`)}
                        unitValueChart=""
                        colorChart="#4BC0C0"
                        thresholdCm={
                          effectiveThresholdCm > 0 ? effectiveThresholdCm : null
                        }
                      />
                    );
                  })()}
                </div>

                {sizeRangeMatrix && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="char-text-16 char-font-semibold char-text-primary">
                        {t("Size distribution matrix")}
                      </h5>
                      <div className="char-text-14 char-text-secondary">
                        {t("Darker = more objects")}
                      </div>
                    </div>

                    {(() => {
                      const ranges = (
                        sizeRangeDistribution.labels || []
                      ).filter((label) => label !== "total");
                      const materials = Object.keys(
                        sizeRangeMatrix || {},
                      ).filter((cls) => {
                        const lower = (cls || "").toLowerCase();
                        return (
                          lower !== "unknown" &&
                          lower !== "inconnu" &&
                          cls !== "AUTRES (FINES)"
                        );
                      });

                      const rows = materials
                        .map((cls) => {
                          const counts = sizeRangeMatrix[cls] || {};
                          const total = ranges.reduce(
                            (sum, range) => sum + (counts[range] || 0),
                            0,
                          );
                          return { cls, counts, total };
                        })
                        .filter((r) => r.total > 0)
                        .sort((a, b) => b.total - a.total);

                      const maxValue = rows.reduce((max, row) => {
                        const rowMax = ranges.reduce(
                          (m, range) => Math.max(m, row.counts[range] || 0),
                          0,
                        );
                        return Math.max(max, rowMax);
                      }, 0);

                      return (
                        <div className="char-reports-matrix-container">
                          <table className="char-reports-matrix-table">
                            <thead className="char-reports-matrix-table-header">
                              <tr>
                                <th className="char-reports-matrix-table-header-cell">
                                  {t("Material")}
                                </th>
                                {ranges.map((range) => (
                                  <th
                                    key={range}
                                    className="char-reports-matrix-table-header-cell char-reports-matrix-table-header-cell-center"
                                  >
                                    {range}m
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => (
                                <tr
                                  key={row.cls}
                                  className="char-reports-matrix-table-body-row"
                                >
                                  <td className="char-reports-matrix-table-body-row-cell char-reports-matrix-table-body-row-cell-material">
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      {row.cls === "AUTRES (FINES)"
                                        ? t("Autres (FINES)")
                                        : t(row.cls)}
                                      {row.cls === "AUTRES (FINES)" && (
                                        <InfoTip widthMaxRem={20}>
                                          <div>{finesLabel}</div>
                                        </InfoTip>
                                      )}
                                    </div>
                                  </td>
                                  {ranges.map((range) => {
                                    const value = row.counts[range] || 0;
                                    const intensity =
                                      maxValue > 0 ? value / maxValue : 0;
                                    const background =
                                      value === 0
                                        ? "transparent"
                                        : `rgba(75, 192, 192, ${0.15 + 0.65 * intensity})`;
                                    const isBelowThreshold =
                                      effectiveThresholdCm > 0 &&
                                      Number(range.split("-")[1]) * 100 <=
                                        effectiveThresholdCm;
                                    const mutedOverlay = isBelowThreshold
                                      ? "rgba(156, 163, 175, 0.18)"
                                      : null;

                                    return (
                                      <td
                                        key={range}
                                        className="char-reports-matrix-table-body-row-cell"
                                        style={{
                                          background:
                                            mutedOverlay && value > 0
                                              ? `linear-gradient(${mutedOverlay}, ${mutedOverlay}), ${background}`
                                              : mutedOverlay
                                                ? mutedOverlay
                                                : background,
                                        }}
                                      >
                                        {value > 0 ? value : ""}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        {/* Fines Distribution Section - only on "Toutes les données" tab and in admin mode */}
        {characterizationType === REGULATION_TYPES.CLASSIC &&
          isAdmin &&
          sizeRangeDistribution &&
          totalFinesVolume !== undefined &&
          totalWasteVolume > 0 && (
            <div className="char-reports-section" style={{ marginTop: "32px" }}>
              <h2 className="char-reports-section-title">
                {t("Fines distribution")}
              </h2>

              {/* Single white background container for all 3 elements */}
              <div className="char-card">
                {/* Volume Chart */}
                <div className="mb-6">
                  <h5 className="char-text-16 char-font-semibold char-text-primary mb-4">
                    {t("Volume")}
                  </h5>
                  <div className="w-full" style={{ height: "200px" }}>
                    <FinesDistributionChart
                      finesProportionVolume={
                        totalWasteVolume > 0
                          ? ((totalFinesVolume || 0) / totalWasteVolume) * 100
                          : 0
                      }
                      finesBelowThresholdVolume={
                        totalWasteVolume > 0
                          ? ((finesBelowThreshold?.volumeObject || 0) /
                              totalWasteVolume) *
                            100
                          : 0
                      }
                      objectsTakenIntoAccountVolume={
                        totalWasteVolume > 0
                          ? ((totalWasteVolume -
                              (totalFinesVolume || 0) -
                              (finesBelowThreshold?.volumeObject || 0)) /
                              totalWasteVolume) *
                            100
                          : 0
                      }
                      effectiveThresholdCm={effectiveThresholdCm}
                      finesLabel={finesLabel}
                      t={t}
                      unitValueChart="%"
                    />
                  </div>
                </div>

                {/* Mass Chart - only if mass data is available */}
                {totalWeight !== "" &&
                  totalFinesMass !== undefined &&
                  totalWasteMass > 0 && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <h5 className="char-text-16 char-font-semibold char-text-primary mb-4">
                        {t("Mass")}
                      </h5>
                      <div className="w-full" style={{ height: "200px" }}>
                        <FinesDistributionChart
                          finesProportionVolume={
                            totalWasteMass > 0
                              ? ((totalFinesMass || 0) / totalWasteMass) * 100
                              : 0
                          }
                          finesBelowThresholdVolume={
                            totalWasteMass > 0
                              ? ((finesBelowThreshold?.massObject || 0) /
                                  totalWasteMass) *
                                100
                              : 0
                          }
                          objectsTakenIntoAccountVolume={
                            totalWasteMass > 0
                              ? ((totalWasteMass -
                                  (totalFinesMass || 0) -
                                  (finesBelowThreshold?.massObject || 0)) /
                                  totalWasteMass) *
                                100
                              : 0
                          }
                          effectiveThresholdCm={effectiveThresholdCm}
                          finesLabel={finesLabel}
                          t={t}
                          unitValueChart="%"
                        />
                      </div>
                    </div>
                  )}

                {/* Legend */}
                <div className="char-reports-legend-container">
                  <div className="char-reports-legend-list">
                    {[
                      {
                        label: t("Fines (proportion)"),
                        color: "#FFA500",
                        volumePercent:
                          totalWasteVolume > 0
                            ? ((totalFinesVolume || 0) / totalWasteVolume) * 100
                            : 0,
                        massPercent:
                          totalWasteMass > 0
                            ? ((totalFinesMass || 0) / totalWasteMass) * 100
                            : 0,
                      },
                      {
                        label:
                          effectiveThresholdCm > 0
                            ? t("Objects below {{threshold}} cm", {
                                threshold: effectiveThresholdCm,
                              })
                            : t("Fines"),
                        color: "#FF6B6B",
                        volumePercent:
                          totalWasteVolume > 0
                            ? ((finesBelowThreshold?.volumeObject || 0) /
                                totalWasteVolume) *
                              100
                            : 0,
                        massPercent:
                          totalWasteMass > 0
                            ? ((finesBelowThreshold?.massObject || 0) /
                                totalWasteMass) *
                              100
                            : 0,
                      },
                      {
                        label: t("Objects taken into account"),
                        color: "#4BC0C0",
                        volumePercent:
                          totalWasteVolume > 0
                            ? ((totalWasteVolume -
                                (totalFinesVolume || 0) -
                                (finesBelowThreshold?.volumeObject || 0)) /
                                totalWasteVolume) *
                              100
                            : 0,
                        massPercent:
                          totalWasteMass > 0
                            ? ((totalWasteMass -
                                (totalFinesMass || 0) -
                                (finesBelowThreshold?.massObject || 0)) /
                                totalWasteMass) *
                              100
                            : 0,
                      },
                    ].map((item, index) => (
                      <div key={index} className="char-reports-legend-item">
                        <div
                          className="char-reports-legend-item-color"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="char-reports-legend-item-label">
                          {item.label}
                        </span>
                        <div className="char-reports-legend-item-values">
                          <span className="char-reports-legend-item-values-item">
                            {t("Volume")}:{" "}
                            <strong className="char-reports-legend-item-values-item-strong">
                              {item.volumePercent.toFixed(1)}%
                            </strong>
                          </span>
                          {totalWeight !== "" && (
                            <span className="char-reports-legend-item-values-item">
                              {t("Mass")}:{" "}
                              <strong className="char-reports-legend-item-values-item-strong">
                                {item.massPercent.toFixed(1)}%
                              </strong>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Volumetric share by material (fp=0) */}
              {totalWasteVolume > 0 && volumetricTableRows.length > 0 && (
                <div
                  className="char-reports-section"
                  style={{ marginTop: "24px" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="char-text-16 char-font-semibold char-text-primary">
                      {t("Volumetric share by material (finesProportion = 0)")}
                    </h5>
                    <button
                      type="button"
                      className="btn btn--primary text-sm"
                      onClick={() => {
                        const breakdown = finesBelowThresholdByMaterial || {};
                        const allCls = new Set([
                          ...Object.keys(keptVolumeByMaterialFp0 || {}),
                          ...Object.keys(breakdown),
                        ]);
                        const materials = Array.from(allCls)
                          .filter((cls) => cls)
                          .map((cls) => {
                            const keptFp0 = keptVolumeByMaterialFp0?.[cls] || 0;
                            const belowThresholdVol =
                              breakdown[cls]?.volumeObject || 0;
                            const volumiqueIA =
                              totalWasteVolume > 0
                                ? (keptFp0 / totalWasteVolume) * 100
                                : 0;
                            const volumiqueIA_lt40cm =
                              totalWasteVolume > 0
                                ? (belowThresholdVol / totalWasteVolume) * 100
                                : 0;
                            return {
                              name: t(cls),
                              referenceName: cls,
                              volumiqueIA: Math.round(volumiqueIA * 100) / 100,
                              volumiqueIA_lt40cm:
                                Math.round(volumiqueIA_lt40cm * 100) / 100,
                            };
                          })
                          .filter(
                            (m) =>
                              m.volumiqueIA > 0 || m.volumiqueIA_lt40cm > 0,
                          );
                        const dateTime = period?.start || period?._time;
                        const d = dateTime ? new Date(dateTime) : null;
                        const exportDate = d
                          ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
                          : "";
                        const exportTime = d
                          ? `${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`
                          : "";
                        const zoneName = zone?.addressName || zone?.name || "";
                        const parsed = clientParsed ?? period?.clientParsed;
                        const wasteOrigin = parsed?.name?.trim() || "";
                        const identificationReference =
                          parsed?.code?.trim() || "";
                        const exportWeight =
                          totalWeight != null && totalWeight !== ""
                            ? Number(totalWeight)
                            : undefined;
                        const json = JSON.stringify(
                          {
                            source: "akanthas-characterization",
                            version: 1,
                            zoneName: zoneName || undefined,
                            wasteOrigin: wasteOrigin || undefined,
                            identificationReference:
                              identificationReference || undefined,
                            date: exportDate || undefined,
                            time: exportTime || undefined,
                            weight: exportWeight,
                            materials,
                          },
                          null,
                          2,
                        );
                        const blob = new Blob([json], {
                          type: "application/json",
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `characterization-fines-${effectiveThresholdCm}cm.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      {t("Export JSON")}
                    </button>
                  </div>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700">
                      {t(
                        "This table only: finesProportion (dust share) is set to 0. Rest of the page uses calculated finesProportion. Below: % of total waste volume per material — above threshold (objects ≥ {{threshold}} cm) and below threshold (objects < {{threshold}} cm).",
                        { threshold: effectiveThresholdCm },
                      )}
                    </p>
                  </div>
                  <div className="overflow-auto border border-gray-200 rounded-lg bg-white">
                    <table className="min-w-[400px] w-full table-fixed border-separate border-spacing-0">
                      <thead className="sticky top-0 bg-white">
                        <tr>
                          <th className="w-[20%] text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-200 bg-white">
                            {t("Material")}
                          </th>
                          <th className="w-[20%] text-right text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-200 bg-white whitespace-nowrap">
                            {t("% volumetric (objects ≥ threshold)")}
                          </th>
                          <th className="w-[20%] text-right text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-200 bg-white whitespace-nowrap">
                            {t("% volumetric (objects < threshold)")}
                          </th>
                          <th className="w-[20%] text-right text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-200 bg-white whitespace-nowrap">
                            {t("Density")}
                          </th>
                          <th className="w-[20%] text-right text-xs font-semibold text-gray-700 px-3 py-2 border-b border-gray-200 bg-white whitespace-nowrap">
                            {t("Fines proportion")} (%)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {volumetricTableRows.map((row) => {
                          const referenceName = row.cls;
                          const material = materialDelivery.find(
                            (m) =>
                              m.cls === referenceName ||
                              m.referenceName === referenceName,
                          );
                          const fromDescription =
                            materialsByReferenceName[referenceName];
                          const densityValue =
                            material?.density ?? fromDescription?.density;
                          const finesProportionValue =
                            material?.finesProportion ??
                            fromDescription?.finesProportion;
                          const density =
                            densityValue != null
                              ? toFrenchNumber(Number(densityValue), 0)
                              : "—";
                          const finesPct =
                            finesProportionValue != null
                              ? `${toFrenchNumber(Number(finesProportionValue) * 100, 0)}%`
                              : "—";
                          return (
                            <tr key={row.cls} className="hover:bg-gray-50">
                              <td className="w-[20%] text-sm font-medium text-gray-800 px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                                {t(row.cls)}
                              </td>
                              <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                                {toFrenchNumber(row.pctAboveThreshold, 2)}%
                              </td>
                              <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                                {toFrenchNumber(row.pctBelowThreshold, 2)}%
                              </td>
                              <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                                {density}
                              </td>
                              <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                                {finesPct}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50 font-semibold">
                        <tr>
                          <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-t-2 border-gray-200 whitespace-nowrap">
                            {t("Total")}
                          </td>
                          <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-t-2 border-gray-200 text-right whitespace-nowrap">
                            {toFrenchNumber(
                              volumetricTableRows.reduce(
                                (s, r) => s + r.pctAboveThreshold,
                                0,
                              ),
                              2,
                            )}
                            %
                          </td>
                          <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-t-2 border-gray-200 text-right whitespace-nowrap">
                            {toFrenchNumber(
                              volumetricTableRows.reduce(
                                (s, r) => s + r.pctBelowThreshold,
                                0,
                              ),
                              2,
                            )}
                            %
                          </td>
                          <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-t-2 border-gray-200 text-right whitespace-nowrap" />
                          <td className="w-[20%] text-sm text-gray-800 px-3 py-2 border-t-2 border-gray-200 text-right whitespace-nowrap" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};
export default CharacterizationReports;
